'use client'
import {useRouter} from 'next/router'
import toast from 'react-hot-toast'
// Context
import {useVoterContext} from '../context/voter'
// Helpers
import {localSignOut, upstreamSignOut} from '../utils/auth-helpers'
import {logDataByLevel} from '../utils/logging-helpers'
// Styles
import navbarStyles from './navbar.module.scss'

interface SilentFederatedSignOutButtonProps {
  buttonText?: string
  isNavbarButton?: boolean
  isSigningOut: boolean
  sendIsSigningOut: (isSigningOut: boolean) => void
  sendVoterMenuIsIn?: ((isIn: boolean) => void) | null
}

const SilentFederatedSignOutButton = ({
  buttonText = 'Sign out',
  isNavbarButton = false,
  isSigningOut,
  sendIsSigningOut,
  sendVoterMenuIsIn = null,
}: SilentFederatedSignOutButtonProps) => {
  const router = useRouter()
  const {setVoter} = useVoterContext()

  const handleSilentFederatedSignOut = async () => {
    if (isSigningOut) return

    try {
      sendIsSigningOut(true)

      localSignOut({setVoter, sendVoterMenuIsIn: sendVoterMenuIsIn ?? undefined})

      const success = await upstreamSignOut({router})

      if (!success) {
        logDataByLevel({
          level: 'warn',
          message: 'Upstream sign-out failed, but local sign-out completed',
          data: {success, router},
        })
        // Local sign-out succeeded (the user's goal), so no error toast — but
        // re-enable the button; there's no redirect on this path, so it would
        // otherwise stay stuck on "Signing out...".
        sendIsSigningOut(false)
      }
    } catch (error) {
      logDataByLevel({
        level: 'error',
        message: 'Error during sign-out process',
        data: error,
      })
      toast.error('Something went wrong signing out. Please refresh the page.')
      // No redirect on failure — re-enable so the button isn't stuck disabled.
      sendIsSigningOut(false)
    }
  }

  return isNavbarButton
    ? <li className={navbarStyles['__dropdown-item']}>
      <button
        className={`${navbarStyles['__dropdown-item-button']} ${isSigningOut ? navbarStyles['--is-disabled'] : ''}`}
        onClick={handleSilentFederatedSignOut}
        disabled={isSigningOut}
      >
        {isSigningOut ? 'Signing out...' : buttonText}
      </button>
    </li>
    : <button
      className='button is-large is-dark'
      onClick={handleSilentFederatedSignOut}
      disabled={isSigningOut}
    >
      {isSigningOut ? 'Signing out...' : 'Sign out'}
    </button>
}

export default SilentFederatedSignOutButton
